import type { CityConfig } from "../hawaii-city/types";

export const kauaiConfig: CityConfig = {
  city: "Kauai",
  state: "Hawaii",
  stateAbbr: "HI",
  slug: "kauai",
  route: "/kauai",
  title: "Emergency & Expedited Passport Services in Kauai, HI — Garden Isle Processing | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services across Kauai, HI. Fast passport renewal and urgent passport help for all Garden Isle residents and visitors. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Kauai, HI",
  heroSubtitle: "All of Kauai — from Lihue to Hanalei, Poipu to Waimea — get your passport fast without leaving the Garden Isle. Start online, ship overnight, and we handle the rest. As fast as 24 hours.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Breathtaking%20Kauai%20Hawaii%20landscape%20with%20Na%20Pali%20Coast%20cliffs%2C%20lush%20green%20tropical%20valleys%2C%20dramatic%20waterfalls%20cascading%20into%20turquoise%20ocean%2C%20warm%20golden%20sunset%20light%2C%20tropical%20vegetation%20and%20palm%20trees%2C%20professional%20travel%20photography%2C%20vibrant%20warm%20colors%20with%20deep%20blue%20ocean%2C%20no%20readable%20text%20or%20signs&width=1800&height=700&seq=kauai-hero-01&orientation=landscape",
  heroImageAlt: "Kauai Hawaii Na Pali Coast landscape with dramatic cliffs and waterfalls",
  geoPosition: "22.0964;-159.5261",
  geoPlacename: "Kauai, Hawaii",
  regionalAgency: {
    name: "Honolulu Passport Agency",
    address: "300 Ala Moana Blvd, Room 1-224, Honolulu, HI 96850",
    distance: "Inter-island flight to Honolulu — about 100 miles",
  },
  nearbyCitiesZips: [
    { name: "Lihue", zips: "96766" },
    { name: "Kapaa", zips: "96746" },
    { name: "Koloa", zips: "96756" },
    { name: "Kalaheo", zips: "96741" },
    { name: "Poipu", zips: "96756" },
    { name: "Hanalei", zips: "96714" },
    { name: "Princeville", zips: "96722" },
    { name: "Kilauea", zips: "96754" },
    { name: "Eleele", zips: "96705" },
    { name: "Hanapepe", zips: "96716" },
    { name: "Waimea", zips: "96796" },
    { name: "Kekaha", zips: "96752" },
    { name: "Anahola", zips: "96703" },
    { name: "Honolulu", zips: "96815" },
    { name: "Kailua", zips: "96734" },
    { name: "Kahului", zips: "96732" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online", desc: "No office visit required to begin. Complete your order from anywhere on Kauai in minutes." },
    { icon: "ri-truck-line", title: "Ship Your Documents", desc: "Use overnight shipping from any Kauai town to send your documents directly to us." },
    { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every application is reviewed before submission to reduce errors and delays." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Access", desc: "We hold reserved submission slots — no public appointment needed for Kauai residents." },
  ],
  testimonials: [
    {
      name: "Catherine B.",
      location: "Princeville, HI",
      rating: 5,
      text: "I had a business trip to Paris in 5 days and my passport was expired. Fast Passport Center processed my renewal from Princeville in 4 days. The shipping from Kauai was seamless and their team kept me updated.",
      service: "Passport Renewal",
    },
    {
      name: "Michael D.",
      location: "Koloa, HI",
      rating: 5,
      text: "Our family trip to Italy was in 2 weeks and my son's passport was nowhere to be found. They walked us through the replacement process from Koloa and had a new passport with time to spare.",
      service: "Lost Passport Replacement",
    },
    {
      name: "Anna K.",
      location: "Hanalei, HI",
      rating: 5,
      text: "Emergency trip to Australia for a family matter. I'm in Hanalei on the North Shore and FPC handled everything remotely. The passport arrived in 5 days. No need to fly to Honolulu for an agency appointment.",
      service: "Expedited Passport",
    },
  ],
  facilities: [
    {
      id: "lihue-usps-kauai",
      name: "Lihue Post Office",
      type: "USPS Passport Acceptance",
      address: "4441 Rice St",
      city: "Lihue",
      state: "HI",
      zip: "96766",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Central Kauai — Lihue",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Appointment required. Call ahead to confirm passport acceptance hours.",
      icon: "ri-mail-line",
    },
    {
      id: "kapaa-usps-kauai",
      name: "Kapaa Post Office",
      type: "USPS Passport Acceptance",
      address: "4-1101 Kuhio Hwy",
      city: "Kapaa",
      state: "HI",
      zip: "96746",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "East Side — about 8 miles north",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "East Side Kauai option. Confirm appointment and photo availability.",
      icon: "ri-mail-line",
    },
    {
      id: "koloa-usps-kauai",
      name: "Koloa Post Office",
      type: "USPS Passport Acceptance",
      address: "5374 Koloa Rd",
      city: "Koloa",
      state: "HI",
      zip: "96756",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "South Shore — about 12 miles south",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "South Shore Kauai option. Confirm appointment and photo availability.",
      icon: "ri-mail-line",
    },
    {
      id: "hanalei-usps-kauai",
      name: "Hanalei Post Office",
      type: "USPS Passport Acceptance",
      address: "5-5155 Kuhio Hwy",
      city: "Hanalei",
      state: "HI",
      zip: "96714",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "North Shore — about 30 miles north",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "North Shore Kauai option. Confirm appointment and photo availability.",
      icon: "ri-mail-line",
    },
  ],
  faqs: [
    {
      q: "How fast can I get a passport on Kauai, HI?",
      a: "Through Fast Passport Center, expedited processing can be as fast as 24 hours. Standard processing through a Kauai post office can take 2–6 months. For Kauai residents, our courier service is the fastest option without flying to Honolulu.",
    },
    {
      q: "Do I need to fly to Honolulu for a passport?",
      a: "No. Most Kauai travelers start online and ship documents via overnight courier. The Lihue, Kapaa, Koloa, or Hanalei Post Offices can serve as acceptance facilities if needed, but you don't need to visit the Honolulu agency.",
    },
    {
      q: "Where is the closest passport acceptance facility on Kauai?",
      a: "The Lihue Post Office at 4441 Rice St and the Kapaa Post Office at 4-1101 Kuhio Hwy both accept passport applications by appointment. Koloa and Hanalei Post Offices are also options for other parts of Kauai.",
    },
    {
      q: "What if I'm a tourist on Kauai and need a passport emergency?",
      a: "We frequently help tourists and vacationers across Kauai who discover passport issues while traveling. We can ship to your resort or hotel address in Lihue, Poipu, Princeville, or Hanalei and coordinate return shipping so you don't miss your flight.",
    },
    {
      q: "Can I get an expedited passport if I've never had one before?",
      a: "Yes, but first-time applicants require an in-person acceptance clerk visit. The Lihue or Kapaa Post Office are the most convenient options for Kauai residents. We guide you through exactly what to bring.",
    },
    {
      q: "How does shipping from Kauai work?",
      a: "You can ship overnight using FedEx or UPS from any town on Kauai — Lihue, Kapaa, Koloa, or Hanalei. Most Kauai residents use the FedEx location near Lihue Airport or the UPS Store in Kukui Grove.",
    },
    {
      q: "Is Fast Passport Center an official government agency?",
      a: "No — Fast Passport Center is a private, approved and registered U.S. Department of State passport courier service. We work within the official passport system to provide expedited processing.",
    },
    {
      q: "What if I need a child passport on Kauai?",
      a: "Child passports (for minors 15 and under) require parental participation and an in-person acceptance step — both parents typically must consent. We provide clear instructions for Kauai families.",
    },
    {
      q: "Can I get a passport if mine is lost or stolen?",
      a: "Yes. Lost or stolen passport replacements often require additional forms (including Form DS-64). We guide Kauai residents through the exact process, including the acceptance clerk visit and expedited submission.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Lihue", address: "3-3300 Kuhio Hwy, Lihue, HI 96766", hours: "Daily 7 AM – 11 PM" },
    { name: "Walgreens — Kapaa", address: "4-1101 Kuhio Hwy, Kapaa, HI 96746", hours: "Daily 7 AM – 11 PM" },
    { name: "CVS Pharmacy — Lihue", address: "3-2600 Kaumualii Hwy, Lihue, HI 96766", hours: "Daily 7 AM – 10 PM" },
    { name: "The UPS Store — Lihue", address: "3-3200 Kuhio Hwy, Lihue, HI 96766", hours: "Call for hours" },
    { name: "Walmart — Lihue", address: "3-3300 Kuhio Hwy, Lihue, HI 96766", hours: "Call for hours" },
  ],
  dmvLocations: [
    {
      name: "Lihue DMV — Kauai County Service Center",
      address: "4444 Rice St, Lihue, HI 96766",
      hours: "Mon–Fri, 7:45 AM – 4:00 PM",
      note: "Driver licenses, REAL ID, state IDs. Appointment recommended.",
      featured: true,
    },
    {
      name: "Kapaa DMV — Kapaa Satellite City Hall",
      address: "4-1577 Kuhio Hwy, Kapaa, HI 96746",
      hours: "Mon–Fri, 7:45 AM – 4:00 PM",
      note: "East Side Kauai option. Appointment recommended.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "LIH",
      name: "Lihue Airport",
      distance: "Primary airport — Kauai",
      address: "3901 Mokulele Loop, Lihue, HI 96766",
      note: "Major airport for Kauai with direct flights to mainland U.S. and inter-island connections. Most Kauai travelers fly from LIH for international trips.",
      flightUrl: "https://www.hawaii.gov/lih/",
      flightLabel: "LIH Airport Website",
      featured: true,
    },
    {
      code: "HNL",
      name: "Daniel K. Inouye International Airport",
      distance: "Inter-island flight — Honolulu",
      address: "300 Rodgers Blvd, Honolulu, HI 96819",
      note: "Major international hub. Many Kauai travelers connect through HNL for international destinations.",
      flightUrl: "https://www.hawaii.gov/hnl/",
      flightLabel: "HNL Airport Website",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport",
      description: "Flying in 24–72 hours? Our emergency courier service is designed for urgent, last-minute travel from anywhere on Kauai.",
      bullets: ["As fast as 24 hours", "Reserved agency submission", "Live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Already have a U.S. passport? We help Kauai travelers renew fast and avoid delays — no agency visit required for most renewals.",
      bullets: ["Eligible for expedited processing", "Ship documents overnight", "Professional document review"],
      badge: "Most Common",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-file-add-line",
      title: "New Passport",
      description: "First-time applicants typically require an in-person acceptance clerk visit. We guide Kauai residents through every step.",
      bullets: ["Step-by-step checklist provided", "Acceptance clerk guidance", "Expedited after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost or Stolen Passport",
      description: "Replacements often require additional forms. We guide you through exactly what's needed to avoid costly mistakes.",
      bullets: ["DS-64 form guidance included", "Replacement processing", "Urgent timelines available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Minors (15 and under) require parental participation and an in-person acceptance step. We provide clear instructions for Kauai families.",
      bullets: ["Both parents must consent", "Clear checklist provided", "Expedited options available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change",
      description: "After marriage, divorce, or a legal name change — we assist with corrected passports for Kauai residents.",
      bullets: ["Marriage certificate guidance", "Legal name change support", "Document review included"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
    {
      icon: "ri-book-2-line",
      title: "Second Passport & Damaged",
      description: "Need a second passport for frequent travel, or replacing a damaged one? We handle both efficiently.",
      bullets: ["Frequent traveler solutions", "Damaged passport replacement", "Expedited processing"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=second-passport",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Kauai travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for Kauai residents with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance for Kauai residents with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for Kauai residents with lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16 on Kauai, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service for Kauai residents after marriage, divorce, or legal name changes." },
    { name: "Second Passport & Damaged", description: "Second passport issuance for frequent Kauai travelers and damaged passport replacement." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Waiting Too Long to Apply", description: "Government processing times change constantly. Many Kauai travelers assume 6–8 weeks is safe, only to find out it's now 3–4 months. Apply as soon as you book travel." },
    { icon: "ri-camera-off-line", title: "Wrong Passport Photo", description: "Photos from drugstores and photo booths often get rejected for lighting, background, or expression issues. We provide exact specs so you get it right the first time." },
    { icon: "ri-file-damage-line", title: "Incomplete Forms", description: "One missing signature or wrong date and your application gets sent back. Our checklist catches errors before submission, saving you weeks of delay." },
    { icon: "ri-pen-nib-line", title: "Wrong Proof of Citizenship", description: "Not all birth certificates are accepted. Some require a long-form version. We tell you exactly which documents you need based on your situation." },
    { icon: "ri-calendar-close-line", title: "Missing Travel Proof for Emergency", description: "Regional agencies require proof of urgent travel. Without it, you're turned away. We help you prepare the right documentation before you need it." },
    { icon: "ri-map-pin-time-line", title: "Not Checking Name Consistency", description: "Your passport name must match your driver's license and ticket exactly. Middle names, suffixes, and hyphenated names cause more rejections than you'd think." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose the processing speed that fits your travel date and complete your secure order. Takes just a few minutes." },
    { number: "02", icon: "ri-file-list-3-line", title: "We Confirm & Send Your Checklist", description: "We confirm your details and provide a personalized checklist, instructions, and document review guidance." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Your Documents Overnight", description: "Send your documents using an overnight shipping option from any Kauai town." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Access", description: "We submit through our reserved passport agency availability — so you don't have to fly to Honolulu." },
    { number: "05", icon: "ri-passport-line", title: "Your Passport Is Completed", description: "Your passport is completed within the timeframe you selected. No guessing, no hoping an appointment opens up." },
  ],
  processingOptions: [
    { speed: "24", unit: "Hours*", label: "Regional Agency Processing", tag: "Emergency Only", tagColor: "bg-gray-100 text-gray-500", features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"], highlight: false, emergency: true },
    { speed: "2–9", unit: "Business Days", label: "Fast Passport Center", tag: "Most Popular", tagColor: "bg-[#F6C344] text-[#1A365D]", features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"], highlight: true },
    { speed: "3–5", unit: "Weeks", label: "Fast Passport Center", tag: "Standard Expedited", tagColor: "bg-[#1A365D]/10 text-[#1A365D]", features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"], highlight: false, smartSaver: true },
    { speed: "1–2", unit: "Months", label: "Self-Service (Post Office)", tag: "Slowest", tagColor: "bg-gray-100 text-gray-400", features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"], highlight: false, muted: true },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — limited availability, often weeks out", fpc: "No appointment needed to start", fpcGood: true },
    { feature: "In-Person Visit", agency: "Required — fly to Honolulu", fpc: "Most travelers ship documents overnight from Kauai", fpcGood: true },
    { feature: "Processing Speed", agency: "Urgent travel only — strict eligibility", fpc: "As fast as 24 hours — multiple speed options", fpcGood: true },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review to reduce delays", fpcGood: true },
    { feature: "Courier Access", agency: "Not available to the public", fpc: "Reserved daily submission availability", fpcGood: true },
    { feature: "Predictability", agency: "Appointments can be cancelled or unavailable", fpc: "Predictable processing based on option selected", fpcGood: true },
    { feature: "Who Can Use It", agency: "Strict eligibility — urgent travel only", fpc: "Most U.S. citizens with required documents", fpcGood: true },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Lihue", route: "/lihue" },
    { name: "Honolulu", route: "/honolulu" },
    { name: "Kailua", route: "/kailua" },
    { name: "Kahului", route: "/kahului" },
    { name: "Hilo", route: "/hilo" },
    { name: "Kailua-Kona", route: "/kailua-kona" },
  ],
  fpcOfficeAddress: "300 Ala Moana Blvd, Room 1-224, Honolulu, HI 96850",
  fpcOfficeNote: "After signing up online, choose what works best for you: courier pickup from your Kauai home, ship with our prepaid label from any UPS/FedEx location in Lihue or Kapaa, or schedule a drop-off. Most Kauai clients ship overnight.",
  fpcOfficeDistance: "Serving all of Kauai remotely",
  serviceAreaIntro: "Whether you are in Lihue, Kapaa, Koloa, Poipu, Hanalei, or anywhere on the Garden Isle, Fast Passport Center makes expedited passport processing simple — no flying to Honolulu, no agency lines.",
  nearbyCitiesIntro: "Anywhere on Kauai — start online, ship overnight from your town, and we handle the rest.",
  comparisonIntro: "",
  howItWorksIntro: "From start to finish, we make getting an expedited passport on Kauai as simple as possible.",
  mistakesIntro: "These mistakes cause the most delays and rejections for Kauai travelers. Fast Passport Center reviews your documents before submission to catch these issues.",
  testimonialsIntro: "Real reviews from real travelers across Kauai who trusted Fast Passport Center with their expedited passports.",
  faqIntro: "Everything Kauai travelers ask us most often about expedited passport services.",
  resourcesIntro: "",
  localTravelThemes: [],
  landmarks: [],
  island: "Kauai",
  islandNickname: "The Garden Isle",
  landmarkCards: [
    {
      name: "Na Pali Coast State Wilderness Park",
      description: "The most photographed coastline in Hawaii, Na Pali's emerald cliffs and hidden sea caves are accessible by boat, air, or foot. International travelers from every continent make this their reason to visit.",
      imageUrl: "https://readdy.ai/api/search-image?query=Na%20Pali%20Coast%20Kauai%20Hawaii%2C%20towering%20green%20sea%20cliffs%20above%20turquoise%20ocean%2C%20waterfall%20cascading%20down%20cliffs%2C%20lush%20tropical%20vegetation%2C%20perfect%20blue%20sky%2C%20professional%20aerial%20travel%20photography%2C%20dramatic%20natural%20wonder%2C%20no%20readable%20text%20or%20signs&width=600&height=400&seq=kauai-napali-full&orientation=landscape",
    },
    {
      name: "Poipu Beach",
      description: "South Kauai's premier beach destination, Poipu Beach is sunny year-round and draws international sunbathers, snorkelers, and travelers who often extend their Kauai stays into Pacific or Asian adventures.",
      imageUrl: "https://readdy.ai/api/search-image?query=Poipu%20Beach%20South%20Kauai%20Hawaii%2C%20sunny%20golden%20sand%20beach%20with%20Hawaiian%20monk%20seals%20resting%2C%20gentle%20turquoise%20waves%2C%20tropical%20palm%20trees%2C%20professional%20travel%20photography%2C%20no%20readable%20text%20or%20signs&width=600&height=400&seq=kauai-poipu&orientation=landscape",
    },
    {
      name: "Hanalei Bay",
      description: "A two-mile crescent bay on Kauai's North Shore, Hanalei is framed by dramatic green mountains. Its legendary surf breaks attract international surfers, and its beauty attracts honeymooners from around the world.",
      imageUrl: "https://readdy.ai/api/search-image?query=Hanalei%20Bay%20Kauai%20Hawaii%20North%20Shore%2C%20crescent%20shaped%20bay%20with%20perfect%20waves%2C%20lush%20green%20mountain%20valley%20backdrop%2C%20taro%20fields%2C%20beautiful%20tropical%20setting%2C%20professional%20travel%20photography%2C%20vibrant%20colors%2C%20no%20readable%20text%20or%20signs&width=600&height=400&seq=kauai-hanalei&orientation=landscape",
    },
  ],
  travelTips: [
    { icon: "ri-flight-takeoff-line", title: "LIH Is Your Gateway", description: "Lihue Airport (LIH) connects Kauai to mainland U.S. directly and internationally through HNL. Check passport expiration 6+ months before any international travel." },
    { icon: "ri-map-pin-line", title: "Kauai Is Passport-Agency-Free", description: "There is no regional passport agency on Kauai. The nearest is in Honolulu. Fast Passport Center is the most convenient way to get an expedited passport without leaving the island." },
    { icon: "ri-leaf-line", title: "Eco & Film Tourism", description: "Kauai's pristine landscapes have hosted over 60 Hollywood productions. Film crews, production staff, and international eco-tourists all flow through the Garden Isle with passport needs." },
    { icon: "ri-hotel-line", title: "North Shore & South Shore Resorts", description: "From St. Regis Princeville to Grand Hyatt Poipu, Kauai’s resorts host international guests year-round. Resort visitors discovering passport issues can ship from Lihue — we return to your hotel." },
    { icon: "ri-truck-line", title: "Ship from Lihue or Kapaa", description: "FedEx near Lihue Airport and UPS at Kukui Grove Shopping Center serve all Kauai residents with overnight shipping. Documents received by 6pm are processed next business day." },
    { icon: "ri-sun-line", title: "Year-Round Destination", description: "Kauai's North Shore is wet in winter, South Shore sunny year-round. The island's 365-day appeal means passport urgency has no off-season — we process year-round." },
  ],
  statePageRoute: "/states/hawaii",
  statePageName: "Hawaii",
  dmvWebsite: "https://www.hawaii.gov/dot/highways/mvm",
  dmvLabel: "Hawaii DMV — Official Website",
  vitalRecordsIcon: "ri-heart-pulse-line",
  vitalRecordsLinks: [
    { label: "Birth & Death Certificates (HI DOH)", url: "https://health.hawaii.gov/vitalrecords/", desc: "Hawaii Department of Health — Office of Health Status Monitoring for birth, death, marriage, and divorce certificates statewide" },
    { label: "Marriage Certificates (HI DOH)", url: "https://health.hawaii.gov/vitalrecords/marriage-certificates/", desc: "Certified copies of Hawaii marriage certificates from the Department of Health Vital Records Office" },
    { label: "Divorce Decrees (HI Family Court)", url: "https://www.courts.state.hi.us/self-help/court-forms/court-forms-family-court", desc: "Hawaii State Judiciary Family Court — divorce decree records by circuit" },
    { label: "VitalChek Online Ordering", url: "https://www.vitalchek.com/", desc: "Order Hawaii birth, death, marriage, and divorce certificates online via VitalChek — fastest state-approved method" },
  ],
};